import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Container, Typography, List, ListItem, ListItemText, IconButton, TextField, Button, Box, Modal } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AssignRoleOrTeam from '../components/AssignRoleorTeam';
import MemberDetailsForm from '../components/MemberDetailsForm';
import { useTranslation } from "react-i18next";

const ProjectMembers = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [deleteMemberModalOpen, setDeleteMemberModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('role');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [memberDetailsModalOpen, setMemberDetailsModalOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [roles, setRoles] = useState([]);
  const [projectOwner, setProjectOwner] = useState(null);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });
  // MemberDetailsForm modalını açma
  const handleOpenMemberDetailsModal = (memberId) => {
    setSelectedMember(memberId);
    setMemberDetailsModalOpen(true);
  };

  // MemberDetailsForm modalını kapatma
  const handleCloseMemberDetailsModal = () => {
    setMemberDetailsModalOpen(false);
  };

  const fetchMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/projects/${projectId}/members`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch project members', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchUserRole();
    fetchRoles();
    fetchMembers();
    fetchProjectOwner();
  }, [fetchMembers]);

  // Rolleri çekme
  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/roles', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Roller alınamadı:', error);
    }
  };

  const fetchProjectOwner = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/projects/${projectId}/owner`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjectOwner(response.data);
    } catch (error) {
      console.error('Failed to fetch project owner:', error);
    }
  }, [projectId]);


  const removeMember = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/projects/${projectId}/members/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setMembers(members.filter((member) => member.id !== userId));
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await api.post(`/api/projects/${projectId}/invite`, { email: inviteEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteMessage(t("invitation_sent"));
      setInviteEmail('');
      setInviteModalOpen(false);
    } catch (error) {
      setInviteMessage(t("invitation_failed") + (error.response?.data?.error || error.message));
    }
  };



  const handleOpenDeleteMemberModal = (memberId) => {
    setSelectedMember(memberId);
    setDeleteMemberModalOpen(true);
  };

  const addToTeam = (memberId) => {
    setSelectedMember(memberId);
    setModalType('team');
    setModalOpen(true);
  };

  const changeRole = (userId) => {
    handleOpenModal('role', userId);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = (type, id = null) => {
    setModalType(type);
    setSelectedMember(id);
    setModalOpen(true);
  };

  const handleCloseDeleteMemberModal = () => {
    setDeleteMemberModalOpen(false);
  };

  const confirmDeleteMember = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/projects/${projectId}/members/${selectedMember}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(members.filter((member) => member.id !== selectedMember));
      handleCloseDeleteMemberModal();
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      // API isteğini yap
      const roleResponse = await api.get(`/api/projects/${projectId}/role`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("rol " + roleResponse.data.roleId);
      if (roleResponse.data.roleId === 1) {
        setIsAuthorized(true);
      }
      else {
        setIsAuthorized(false);
      }
    } catch (error) {
      // Hata oluşursa yanıtı veya hata mesajını yazdır
      console.error('Error fetching role:', error.response?.data || error.message);
    }
  };

  

  const getRoleNameById = (roleId) => {
    if (!roleId || roles.length === 0) return "";
  
    const role = roles.find(r => Number(r.id) === Number(roleId));
    return role ? role.name : "Unknown Role";
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Container maxWidth="md">
      <Box className='box-half'>
  <Typography variant="h6" className='header'>
    <span className='header-text'>{t('projectMembers.title')}</span>
    {isAuthorized && (
      <Button variant="contained" className='action-button' color="primary" onClick={() => setInviteModalOpen(true)}>
        {t('projectMembers.inviteUser')}
      </Button>
    )}
  </Typography>
  
  {projectOwner && (
    <Box className="project-owner">
      <Typography variant="subtitle1">
        <strong>{t('projectMembers.owner')}:</strong> <span>{projectOwner.name}</span>
      </Typography>
      <Typography variant="subtitle2" style={{ marginTop: '8px' }}>
        <strong>{t('projectMembers.email')}:</strong> <span>{projectOwner.email}</span>
      </Typography>
      <a href={`mailto:${projectOwner.email}`}>{t('projectMembers.contactOwner')}</a>
    </Box>
  )}
  
  <List className='list'>
    {members.map((member) => (
      <ListItem className='list-item' key={member.id}>
        <ListItemText 
          primary={<span className="list-item-text">{member.name}</span>}
          secondary={<span className="list-item-text"> {`${member.email} - ${t('projectMembers.role')}: ${getRoleNameById(member.roleId)}`}</span>} 
        />
        {isAuthorized && (
          <IconButton onClick={() => handleOpenDeleteMemberModal(member.id)}>
            <DeleteIcon />
          </IconButton>
        )}
        <IconButton onClick={() => handleOpenMemberDetailsModal(member.id)}>
          <EditIcon />
        </IconButton>
      </ListItem>
    ))}
  </List>
</Box>

{/* Invite User Modal */}
<Modal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)}>
  <Box style={{ backgroundColor: 'white', padding: '21px', margin: '50px auto', width: '400px' }}>
    <Typography variant="h5" gutterBottom>
      {t('projectMembers.inviteUser')}
    </Typography>
    <form onSubmit={handleInvite}>
      <TextField
        label={t('projectMembers.userEmail')}
        fullWidth
        margin="normal"
        value={inviteEmail}
        onChange={(e) => setInviteEmail(e.target.value)}
      />
      <Button variant="contained" color="primary" type="submit" fullWidth>
        {t('projectMembers.sendInvitation')}
      </Button>
      {inviteMessage && <Typography color="error">{inviteMessage}</Typography>}
    </form>
  </Box>
</Modal>

{/* Delete Member Modal */}
<Modal open={deleteMemberModalOpen} onClose={handleCloseDeleteMemberModal}>
  <Box style={{ backgroundColor: 'white', padding: '20px', margin: '50px auto', width: '400px' }}>
    <Typography variant="h5" gutterBottom>
      {t('projectMembers.confirmDeletion')}
    </Typography>
    <Typography variant="body1" gutterBottom>
      {t('projectMembers.deleteConfirmation')}
    </Typography>
    <Button onClick={confirmDeleteMember} variant="contained" color="secondary" fullWidth>
      {t('projectMembers.delete')}
    </Button>
    <Button onClick={handleCloseDeleteMemberModal} variant="contained" color="primary" fullWidth>
      {t('projectMembers.cancel')}
    </Button>
  </Box>
</Modal>


      {/* Assign Role or Team Modal */}
      <AssignRoleOrTeam
        open={modalOpen && (modalType === 'role' || modalType === 'team')}
        onClose={handleCloseModal}
        projectId={projectId}
        memberId={selectedMember}
        type={modalType}
        refreshData={fetchMembers}
      />
      {/* Member Details Form Modal */}
      {memberDetailsModalOpen && (
        <MemberDetailsForm
          open={memberDetailsModalOpen}
          onClose={handleCloseMemberDetailsModal}
          memberId={selectedMember}
          projectId={projectId}
          refreshData={fetchMembers} // Üye bilgilerini güncellemek için
        />
      )}
    </Container>
  );
};

export default ProjectMembers;
